import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DAYS } from "@/lib/breaking-free";
import {
  getBreakingFreeContent,
  saveBreakingFreeContent,
} from "@/lib/breaking-free.functions";

type DayOverride = {
  title?: string;
  focus?: string[];
  pause?: string;
  reflection?: string;
  affirmation?: string;
};

export const Route = createFileRoute("/admin_/breaking-free")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Breaking Free content — Admin | Recovery Trauma™" },
      { name: "description", content: "Edit the wording of the Breaking Free journey days." },
      { property: "og:title", content: "Breaking Free content — Admin" },
      { property: "og:description", content: "Edit the wording of the Breaking Free journey days." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminBreakingFree,
});

function AdminBreakingFree() {
  const contentFn = useServerFn(getBreakingFreeContent);
  const saveFn = useServerFn(saveBreakingFreeContent);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(1);
  const [draft, setDraft] = useState<DayOverride | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["breaking-free", "content"],
    queryFn: () => contentFn() as Promise<{ overrides: Record<string, DayOverride> }>,
  });

  const base = DAYS.find((d) => d.day === selected)!;
  const stored = data?.overrides?.[`day-${selected}`] ?? {};

  const value = useMemo<DayOverride>(
    () =>
      draft ?? {
        title: stored.title ?? base.title,
        focus: stored.focus ?? base.focus,
        pause: stored.pause ?? base.pause,
        reflection: stored.reflection ?? base.reflection,
        affirmation: stored.affirmation ?? base.affirmation,
      },
    [draft, stored, base],
  );

  const save = useMutation({
    mutationFn: async () =>
      saveFn({
        data: {
          contentKey: `day-${selected}`,
          data: {
            title: value.title ?? "",
            focus: value.focus ?? [],
            pause: value.pause ?? "",
            reflection: value.reflection ?? "",
            affirmation: value.affirmation ?? "",
          },
        },
      }),
    onSuccess: () => {
      toast.success(`Day ${selected} saved`);
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ["breaking-free", "content"] });
    },
    onError: () => toast.error("That didn't save. Please try again."),
  });

  return (
    <div className="mx-auto w-full max-w-4xl px-4 pb-20 pt-8">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Admin
      </Link>
      <h1 className="mt-4 text-2xl font-semibold">Breaking Free — journey content</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Edit the wording members see for each day. Exercises and member writing are untouched.
        Leaving a field as it is keeps the book wording.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {DAYS.map((d) => (
          <button
            key={d.day}
            type="button"
            onClick={() => {
              setSelected(d.day);
              setDraft(null);
            }}
            className={`min-h-[44px] min-w-[52px] rounded-xl border px-3 text-sm ${
              d.day === selected
                ? "border-gold bg-gold-light/50 font-semibold"
                : "border-border bg-background"
            }`}
          >
            {d.day}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : (
        <div className="mt-8 space-y-5 rounded-3xl border border-border bg-card p-5">
          <div>
            <Label htmlFor="bf-title">Title</Label>
            <Input
              id="bf-title"
              className="mt-2 min-h-[48px]"
              value={value.title ?? ""}
              onChange={(e) => setDraft({ ...value, title: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="bf-focus">Today's focus (one paragraph per line)</Label>
            <Textarea
              id="bf-focus"
              rows={8}
              className="mt-2"
              value={(value.focus ?? []).join("\n\n")}
              onChange={(e) =>
                setDraft({
                  ...value,
                  focus: e.target.value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
                })
              }
            />
          </div>
          <div>
            <Label htmlFor="bf-pause">Pause &amp; notice</Label>
            <Textarea
              id="bf-pause"
              rows={3}
              className="mt-2"
              value={value.pause ?? ""}
              onChange={(e) => setDraft({ ...value, pause: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="bf-reflection">Today's reflection</Label>
            <Textarea
              id="bf-reflection"
              rows={3}
              className="mt-2"
              value={value.reflection ?? ""}
              onChange={(e) => setDraft({ ...value, reflection: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="bf-affirmation">Take this with you</Label>
            <Textarea
              id="bf-affirmation"
              rows={2}
              className="mt-2"
              value={value.affirmation ?? ""}
              onChange={(e) => setDraft({ ...value, affirmation: e.target.value })}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              className="min-h-[48px]"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? "Saving…" : `Save day ${selected}`}
            </Button>
            <Button variant="outline" className="min-h-[48px]" onClick={() => setDraft(null)}>
              Reset changes
            </Button>
            <Button asChild variant="ghost" className="min-h-[48px]">
              <Link to="/breaking-free/day/$day" params={{ day: String(selected) }}>
                Preview day
              </Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
